import { ATTRIBUTION_MODEL_FIELDS } from "./configuration";
/**
 * Field-combination rules for the attribution explores: combinations Looker
 * will happily run and return numbers for that do not mean what they appear to.
 *
 * Every rule here decides only — callers render their own message, because the
 * SDK query builder throws and the API returns a 400 naming the filter keys the
 * caller wrote. Rules that depend on the caller's organization live in
 * `policy.ts`; rules about a single value's shape live with the surface that
 * parses it.
 */
/**
 * Grouping-only dimensions — currently just the channel breakdown — are exempt
 * from every rule here. Changing the grain of a result set is not the same as
 * mixing a second kind of measurement into it, so it cannot invalidate the
 * query it groups.
 */
const isExempt = (field) => ATTRIBUTION_MODEL_FIELDS[field]?.isGroupingOnlyDimension === true;
/** The first footfall rule this field set breaks, if any. */
export const getFootfallIncompatibility = (fields) => {
    const hasFootfallFields = fields.some((field) => ATTRIBUTION_MODEL_FIELDS[field]?.isFootfallField);
    if (!hasFootfallFields) {
        return undefined;
    }
    const incompatible = fields.filter((field) => {
        const config = ATTRIBUTION_MODEL_FIELDS[field];
        return (config && !config.isFootfallField && !config.canBeQueriedWithFootfall && !isExempt(field));
    });
    if (incompatible.length > 0) {
        return { reason: "incompatible-with-footfall", fields: incompatible };
    }
    const hasConversionTimeFields = fields.some((field) => ATTRIBUTION_MODEL_FIELDS[field]?.isConversionTime);
    if (hasConversionTimeFields) {
        const impressionTime = fields.filter((field) => !ATTRIBUTION_MODEL_FIELDS[field]?.isConversionTime && !isExempt(field));
        if (impressionTime.length > 0) {
            return {
                reason: "conversion-time-mixed-with-impression-time",
                fields: impressionTime
            };
        }
    }
    return undefined;
};
/**
 * `isExclusiveFrequencyDimension` is declared on the field configs but the SDK
 * query builder never enforced it; this is the shared decision so it can.
 */
export const getFrequencyDimensionConflict = (fields) => {
    const frequencyFields = fields.filter((field) => ATTRIBUTION_MODEL_FIELDS[field]?.isExclusiveFrequencyDimension);
    if (frequencyFields.length === 0) {
        return undefined;
    }
    if (frequencyFields.length > 1) {
        return {
            reason: "multiple-frequency-dimensions",
            frequencyFields,
            otherDimensions: []
        };
    }
    const frequencySet = new Set(frequencyFields);
    const otherDimensions = fields.filter((field) => ATTRIBUTION_MODEL_FIELDS[field]?.type === "dimension" &&
        !frequencySet.has(field) &&
        !isExempt(field));
    if (otherDimensions.length > 0) {
        return { reason: "combined-with-other-dimensions", frequencyFields, otherDimensions };
    }
    return undefined;
};
/**
 * Beacon dimensions and other conversion-scoped fields (`isConversionField`,
 * e.g. `daysToConversion`) aggregate at attribution level; impression-sourced
 * metrics are delivery-level, so a row grouped by both double-counts delivery.
 */
export const getBeaconImpressionMetricConflict = (fields) => {
    const beaconFields = [];
    const impressionMetrics = [];
    for (const field of fields) {
        const config = ATTRIBUTION_MODEL_FIELDS[field];
        if (!config) {
            continue;
        }
        if (config.isBeaconField || config.isConversionField) {
            beaconFields.push(field);
        }
        if (config.metricSource === "impression") {
            impressionMetrics.push(field);
        }
    }
    if (beaconFields.length === 0 || impressionMetrics.length === 0) {
        return undefined;
    }
    return { beaconFields, impressionMetrics };
};
/**
 * The metrics from the list that make sense for the requested dimensions: a
 * metric with an `onlyWithDimensions` rule is kept only when at least one of
 * those dimensions is requested.
 */
export const filterMetricsByDimensions = (metrics, dimensions) => metrics.filter((metric) => {
    const allowed = ATTRIBUTION_MODEL_FIELDS[metric]?.onlyWithDimensions;
    return !allowed || dimensions.some((dimension) => allowed.includes(dimension));
});
//# sourceMappingURL=compatibility.js.map